import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/store/hooks";
import { STATUS } from "@/constants";
import { loadChatSessions } from "@/features/chat/model/chatHistorySlice";
import type { ChatSessionSummary } from "@/features/chat/model/chatHistoryTypes";
import { filterChatSessionsByTitle } from "@/features/chat/model/filterChatSessions";
import { ChatHistoryCard } from "@/features/chat/ui/ChatHistoryCard";
import { ChatHistorySearchToolbar } from "@/features/chat/ui/ChatHistorySearchToolbar";
import { DeleteChatConfirmDialog } from "@/features/chat/ui/DeleteChatConfirmDialog";
import { EditChatTitleModal } from "@/features/chat/ui/EditChatTitleModal";

export function ChatHistoryPage() {
  const { t: tCommon } = useTranslation("common");
  const { t } = useTranslation("chat");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { sessions, listStatus, error } = useAppSelector((state) => state.chatHistory);
  const [searchQuery, setSearchQuery] = useState("");
  const [editSession, setEditSession] = useState<ChatSessionSummary | null>(null);
  const [deleteSession, setDeleteSession] = useState<ChatSessionSummary | null>(null);

  useEffect(() => {
    void dispatch(loadChatSessions());
  }, [dispatch]);

  const untitledLabel = t("history.untitled");
  const filteredSessions = useMemo(
    () => filterChatSessionsByTitle(sessions, searchQuery, untitledLabel),
    [sessions, searchQuery, untitledLabel],
  );

  const handleNewChat = useCallback(() => {
    navigate("/chat", { state: { resetAt: Date.now() } });
  }, [navigate]);

  const isLoading = listStatus === STATUS.LOADING;
  const hasSessions = sessions.length > 0;
  const hasFilteredSessions = filteredSessions.length > 0;

  return (
    <Box
      sx={{
        width: "100%",
      }}
    >
      <Helmet>
        <title>{tCommon("routes.chatHistory")}</title>
      </Helmet>

      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 700,
          letterSpacing: -0.5,
          color: "var(--theme-text-primary)",
        }}
      >
        {t("history.pageTitle")}
      </Typography>

      <Box sx={{ mt: 4 }}>
        <ChatHistorySearchToolbar
          searchPlaceholder={t("history.searchPlaceholder")}
          newChatLabel={t("history.newChat")}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          onNewChat={handleNewChat}
        />
      </Box>

      <Box sx={{ mt: 4 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6} role="status" aria-busy>
            <CircularProgress />
          </Box>
        ) : null}

        {!isLoading && error ? <Alert severity="error">{error}</Alert> : null}

        {!isLoading && !error && !hasSessions ? (
          <Typography sx={{ color: "var(--theme-text-secondary)" }}>
            {t("history.empty")}
          </Typography>
        ) : null}

        {!isLoading && !error && hasSessions && !hasFilteredSessions ? (
          <Typography sx={{ color: "var(--theme-text-secondary)" }}>
            {t("history.noSearchResults")}
          </Typography>
        ) : null}

        {!isLoading && !error && hasFilteredSessions ? (
          <Stack spacing={2}>
            {filteredSessions.map((session) => (
              <ChatHistoryCard
                key={session.sessionId}
                session={session}
                untitledLabel={untitledLabel}
                terminatedLabel={t("history.terminated")}
                menuAriaLabel={t("history.menuAria", {
                  title: session.title?.trim() || untitledLabel,
                })}
                editTitleLabel={t("history.editTitle")}
                deleteLabel={t("history.delete")}
                onEditTitle={setEditSession}
                onDelete={setDeleteSession}
              />
            ))}
          </Stack>
        ) : null}
      </Box>

      <EditChatTitleModal
        open={editSession !== null}
        session={editSession}
        onClose={() => setEditSession(null)}
      />

      <DeleteChatConfirmDialog
        open={deleteSession !== null}
        session={deleteSession}
        onClose={() => setDeleteSession(null)}
      />
    </Box>
  );
}
